import React, { useEffect, useState } from 'react';
import useBoardContentWidgetData from '../../../hooks/useBoardContentWidgetData';
import useBoardContentWidget from '../../../hooks/useBoardContentWidget';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import useTranslate from '../../../../../common/hooks/useTranslate';
import useBoardContentObject from '../../../hooks/useBoardContentObject';
import useExcelExportFormat from '../../../../ui/table/hooks/useExcelExportFormat';
import { extendDataItem, mapTree, TreeList, treeToFlat } from '@progress/kendo-react-treelist';
import { POPUP } from '../../../../../redux/types';
import { useDispatch } from 'react-redux';
import useWidgetUtils from '../../../hooks/useWidgetUtils';
import { useParams } from 'react-router-dom';

import { IBoardNote } from '../../../types/IBoardNote';
import { IBoardObjectNode } from '../../../types/IBoardObjectNode';
import { IBoardMeasure } from '../../../types/IBoardMeasure';
import { IWidgetData } from '../../../types/IWidgetData';
import { WidgetProps } from '../../../types/IWidgetProps';
import { ICurrency } from '../../../types/ICurrency';
import { ITreelistPreparedData } from './types/ITreelistPreparedData';
import { ITreelistColumn } from './types/ITreelistColumn';
import { IGridSetup } from '../grid/types/IGridSetup';
import { IGridDefinition } from '../grid/types/IGridDefinition';

import Icon from '../../../../ui/icon/icon';
import Tabs from '../../../../ui/tabs/tabs';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import NumericCell from '../../cells/numericCell/numericCell';
import CashflowCell from '../cashflow/cells/cashflowCell/cashflowCell';
import WidgetWrapper from '../widgetWrapper';
import ContentLoading from '../../../../ui/loaders/contentLoading/contentLoading';

import './treelistWidget.scss';

const TreelistWidget: React.FC<WidgetProps> = ({
                                                 widget,
                                                 selectedObject,
                                                 selectedPeriod,
                                                 selectedCurrency,
                                                 boardData,
                                                 scenarios,
                                                 notes,
                                                 customisation,
                                                 moveWidget,
                                                 content,
                                                 removeWidget,
                                                 handleLink,
                                                 homeContent,
                                                 widgetView,
                                                 handlePinWidget,
                                                 handleUnpinWidget,
                                                 handleUnpinTrigger,
                                                 unpinId,
                                               }) => {
  const [localCurrency, setLocalCurrency] = useState<ICurrency>();
  const [treelistData, setTreelistData] = useState<ITreelistPreparedData[]>([]);
  const [treelistTabs, setTreelistTabs] = useState<string[]>([]);
  const [widgetCurrencies, setWidgetCurrencies] = useState<ICurrency[]>([]);
  const [nodes, setNodes] = useState<IBoardObjectNode[]>([]);
  const [nodeGroups, setNodeGroups] = useState<IBoardObjectNode[]>([]);
  const [treelistColumns, setTreelistColumns] = useState<ITreelistColumn[]>([]);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [treelistNotes, setTreelistNotes] = useState<IBoardNote[]>([]);
  const [expanded, setExpanded] = useState<any[]>([]);
  const [sort, setSort] = useState<SortDescriptor[]>([{ field: 'name', dir: 'asc' }]);
  const [totalWidth, setTotalWidth] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);

  let objectValueMap = new Map<number, any>();

  const { defineNodes, generateCurrencies, fetchData } = useBoardContentWidgetData();
  const { openCashflowDialog } = useWidgetUtils('treelist', boardData?.measures || [], objectValueMap, homeContent);
  const { findNode } = useBoardContentObject();
  const { widgetSetup } = useBoardContentWidget();
  const { t } = useTranslate();
  const excelFormat = useExcelExportFormat();
  const dispatch = useDispatch();
  const params = useParams();

  let exportItem;
  const subItemsField = 'childNodes';
  const expandField = 'expanded';

  const CashflowCellRender = (column, localCurrency) => {
    return (props) => (
      <CashflowCell
        {...props}
        scale={column.scale}
        columnName={column.name}
        selectedCurrency={localCurrency}
        boardId={params.boardId || boardData.boardId}
        singlePeriod={true}
        investorObjectId={params.investorId || widget.investorObjectId}
        handleCashflowOpen={openCashflowDialog}
      />
    );
  };

  const NumericCellRender = (scale) => {
    return (props) => <NumericCell scale={scale} {...props} />;
  };

  const handleLocalCurrencyChange = (e) => {
    setLocalCurrency(e.value);

    let wSetup = widgetSetup(widget);

    if (wSetup) {
      generateColumnsForSection(wSetup, e.value);
    }

    if (selectedObject && selectedPeriod) {
      setLoading(true);

      fetchData(widget, nodes, boardData?.measures || [], nodeGroups, selectedPeriod, e.value.currency || '', true)
        .then((data) => {
          let treeNodes: IBoardObjectNode[] = [];

          nodes.forEach((node) => {
            let nodeFound = findNode(selectedObject.nodes, node.id || 0);
            if (nodeFound) treeNodes.push(nodeFound);
          });

          nodeGroups.forEach((group) => {
            let groupFound = findNode(selectedObject.nodes, group.id || 0);
            if (groupFound) treeNodes.concat(groupFound.childNodes || []);
          });

          let preparedData = prepareData(data, treeNodes);
          preparedData = formatPreparedData(preparedData);

          if (preparedData && preparedData.length > 0) {
            setTreelistData(preparedData);
          }

          setLoading(false);
        })
        .catch((err) => {
          dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message });
        });
    }
  };

  const onExcelExport = () => {
    const data = treeToFlat(addExpandField(treelistData), expandField, subItemsField);
    const workbook = exportItem.workbookOptions(data, treelistColumns);
    const rows = workbook?.sheets[0].rows;

    rows?.forEach((row: any) => {
      if (row.type === 'data') {
        row.cells.forEach((cell: any, index: number) => {
          let col = treelistColumns[index - 1];
          const measure = boardData.measures?.find((m) => m.id === col?.measureId);
          if (cell.value && cell.value.hasOwnProperty('rowAmount')) {
            cell.format = cell.value.isPercent ? excelFormat.percentFormat(measure?.scale || cell.value.scale || 0) : excelFormat.numberFormat(col?.scale || cell.value.scale || 0);
            cell.value = cell.value.rowAmount;
          }
        });
      }
    });

    exportItem.save(workbook);
  };

  const toggleGridTab = (id: number) => {
    setSelectedTab(id);
  };

  const onExpandChange = (e) => {
    setExpanded(e.value ? expanded.filter((id) => id !== e.dataItem.nodeId) : [...expanded, e.dataItem.nodeId]);
  };

  const fieldNameFor = (type: string, id: number): string => {
    return type + '_' + id;
  };

  const extractTreelistTabs = (setup: IGridSetup) => {
    let treelistTabsArray: string[] = [];

    setup?.definitions?.forEach((definition: IGridDefinition) => {
      if (definition.name) {
        treelistTabsArray.push(definition.name);
      }
    });

    setTreelistTabs(treelistTabsArray);
  };

  const flattenNodesForDataTree = (node: IBoardObjectNode, data: ITreelistPreparedData[], parentNode?: IBoardObjectNode) => {
    let objectValue = {
      row: {
        objectId: node.objectId,
        name: node.name,
        nodeId: node.id,
        parentNodeId: parentNode ? parentNode.id : 0,
        currency: node.currency,
      },
      map: {},
      node: node,
    };
    objectValueMap.set(node.id || 0, objectValue);

    if (objectValue.row) {
      data.push(objectValue.row);
    }

    if (node.childNodes) {
      for (let i = 0; i < node.childNodes.length; i++) flattenNodesForDataTree(node.childNodes[i], data, node);
    }
  };

  const createDataValueInfo = (objectValue: any, row: IWidgetData, node: IBoardObjectNode, measure: IBoardMeasure) => {
    objectValue.map[measure.id || 0] = {
      nodeId: node.id,
      scenarioId: node.scenarioId,
      valueId: row.id,
    };
  };

  const prepareData = (data: IWidgetData[], nodes: IBoardObjectNode[]): ITreelistPreparedData[] => {
    let preparedData: ITreelistPreparedData[] = [];
    objectValueMap.clear();

    for (let j = 0; j < nodes.length; j++) {
      flattenNodesForDataTree(nodes[j], preparedData);
    }

    for (let i = 0; i < data.length; i++) {
      let row = data[i];
      let measure = boardData?.measures?.find((m) => m.id === row.measureId);
      if (measure) {
        let field = fieldNameFor('measure', measure?.id || 0);
        let objectValue = objectValueMap.get(row['nodeId']);
        if (objectValue) {
          let tempMeasure = boardData?.measures?.find((item) => item.id === measure?.id);
          let isPercent = tempMeasure && tempMeasure.isPercent;
          let scale = tempMeasure && tempMeasure.scale;
          objectValue.row[field] = {
            rowAmount: row.amount,
            isPercent: isPercent,
            scale: scale,
          };
          createDataValueInfo(objectValue, row, objectValue.node, measure);
        }
      }
    }
    return preparedData;
  };

  const formatPreparedData = (preparedData: ITreelistPreparedData[]): ITreelistPreparedData[] => {
    let hierarchyData: ITreelistPreparedData[] = [];

    for (let i = 0; i < preparedData.length; i++) {
      let tempElement = preparedData[i];
      let tempArray: ITreelistPreparedData[] = [];

      for (let j = 0; j < preparedData.length; j++) {
        if (preparedData[j].parentNodeId === tempElement.nodeId) {
          tempArray.push(preparedData[j]);
        }
      }

      tempElement.childNodes = tempArray;

      if (!tempElement.parentNodeId) {
        hierarchyData.push(tempElement);
      }
    }

    return hierarchyData;
  };

  const addExpandField = (dataTree: ITreelistPreparedData[]) => {
    return mapTree(dataTree, subItemsField, (item) =>
      extendDataItem(item, subItemsField, {
        [expandField]: expanded.includes(item.nodeId),
      }),
    );
  };

  const processData = () => {
    return addExpandField(treelistData);
  };

  const generateColumnsForSection = (setup: IGridSetup, currency?: ICurrency) => {
    let columnsToMap = (setup?.definitions && setup.definitions[selectedTab].columns) || [];
    let treeWidth = 300;

    let columns: ITreelistColumn[] = [
      {
        field: 'name',
        title: t('WIDGET.GRID_TITLE'),
        locked: !(window.innerWidth < 960),
        expandable: true,
        width: '300px',
        className: 'treelist-name-cell',
      },
    ];

    if (currency && currency.currency === '_') {
      columns.push({
        field: 'currency',
        title: t('WIDGET.GRID_CURRENCY'),
        width: '100px',
      });
    }

    columnsToMap.forEach((column) => {
      let treelistNotes: IBoardNote[] = [];
      let hasNotes = false;

      if (notes) {
        for (let j = 0; j < notes.length; j++) {
          let measureIds = notes[j].measures?.map((m) => m.measureId);

          if (measureIds?.includes(column.measureId)) {
            treelistNotes.push(notes[j]);
            hasNotes = true;
          }
        }
      }

      columns.push({
        field: `measure_${column.measureId}`,
        title: column.name,
        measureId: column.measureId,
        width: 200,
        cell: column.viewCashflows ? CashflowCellRender(column, currency) : NumericCellRender(column.scale),
        hasNotes: hasNotes,
        scale: column.scale,
        notes: treelistNotes,
      });

      treeWidth += 225;
    });

    setTreelistColumns(columns);
    setTotalWidth(`${treeWidth}px`);
  };

  // useEffect(() => {
  //     setLocalCurrency(selectedCurrency);
  // }, [selectedCurrency]);

  useEffect(() => {
    if (notes) {
      setTreelistNotes(notes);
    }
  }, [notes, widget]);

  useEffect(() => {
    let wSetup = widgetSetup(widget);

    if (wSetup) {
      extractTreelistTabs(wSetup);
      generateColumnsForSection(wSetup, selectedCurrency);
    }

    setLocalCurrency(selectedCurrency);

    if (selectedObject && selectedPeriod && selectedCurrency) {
      const { nodes, groups } = defineNodes(widget, selectedObject);
      setNodes(nodes);
      setNodeGroups(groups);

      if (scenarios) {
        setWidgetCurrencies(generateCurrencies(widget, nodes, scenarios));
      }

      fetchData(widget, nodes, boardData?.measures || [], groups, selectedPeriod, selectedCurrency.currency || '', true)
        .then((data) => {
          let treeNodes: IBoardObjectNode[] = [];

          nodes.forEach((node) => {
            let nodeFound = findNode(selectedObject.nodes, node.id || 0);
            if (nodeFound) treeNodes.push(nodeFound);
          });

          groups.forEach((group) => {
            let groupFound = findNode(selectedObject.nodes, group.id || 0);
            if (groupFound) treeNodes.concat(groupFound.childNodes || []);
          });

          let preparedData = prepareData(data, treeNodes);
          preparedData = formatPreparedData(preparedData);

          if (preparedData && preparedData.length > 0) {
            setTreelistData(preparedData);
          }

          setLoading(false);
        })
        .catch((err) => {
          dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message });
        });
    }
  }, [selectedObject, selectedPeriod, selectedTab, selectedCurrency]);

  return (
    <WidgetWrapper
      widget={widget}
      localCurrency={localCurrency}
      widgetCurrencies={widgetCurrencies}
      handleLocalCurrencyChange={handleLocalCurrencyChange}
      notes={treelistNotes}
      moveWidget={moveWidget}
      removeWidget={removeWidget}
      content={content}
      selectedObject={selectedObject}
      customisation={customisation}
      handleLink={handleLink}
      homeContent={homeContent}
      widgetView={widgetView}
      handleUnpin={handleUnpinTrigger}
      handleUnpinFromBoardCallback={handleUnpinWidget}
      handlePinCallback={handlePinWidget}
      unpinId={unpinId}
      renderIcons={<Icon onClick={onExcelExport} name='far fa-file-excel' className='widget-icon pointer' />}>
      {loading ? (
        <ContentLoading />
      ) : (
        <div className='treelist-widget-wrapper'>
          {treelistTabs.length > 1 && <Tabs tabsArray={treelistTabs} onClick={toggleGridTab} />}
          <ExcelExport ref={(exporter) => (exportItem = exporter)} hierarchy={true}
                       fileName={widget.name || t('EXPORT')}>
            <TreeList
              className={`numeric-table-style-wrapper ${treelistTabs.length > 1 ? 'k-grid-with-tabs' : 'k-grid-without-tabs'}`}
              expandField={expandField}
              subItemsField={subItemsField}
              onExpandChange={onExpandChange}
              data={orderBy(processData(), sort)}
              resizable={true}
              scrollable={'scrollable'}
              sortable={true}
              sort={sort}
              tableProps={{ style: { width: totalWidth } }}
              onSortChange={(sortEvent) => {
                setSort(sortEvent.sort);
              }}
              columns={treelistColumns}
            />
          </ExcelExport>
        </div>
      )}
    </WidgetWrapper>
  );
};

export default TreelistWidget;
